// CTV-682: "Advertise on streaming TV" — the leave-behind a rep gives a
// prospective advertiser. Revised against station feedback (KITV, Aug 2026);
// the version this replaced is kept in ./originals for comparison.
//
// What the revision changed and why:
//   • Step 1 leads on self-serve + price rather than describing form fields.
//     Price is our clearest advantage: Roku's self-serve minimum is $500, ours
//     starts at $250.
//   • Step 3 is the purchase, not the payoff. The devices moved into the
//     "why streaming TV" card, which now carries the station's own framing.
//   • "Your local team" names the newsroom explicitly. Built-in ad creation is
//     table stakes in 2026 (MNTN, Vibe, Roku, Paramount all ship one); being
//     the local station your customers already watch is the part nobody else
//     can claim.
//
// Nothing here promises audience or demographic targeting (we sell geography
// only) or outcome metrics (we report plays, completion rate, and spend).

import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { PublisherConfig } from '../../../../../types/selfService';
import MaterialSheet from '../../MaterialSheet';
import { storefrontHref, storefrontUrl } from '../../storefrontUrl';
import { displayedName } from '../../../../../data/publisherConfigs';

const AdvertiserOnePagerV2: React.FC<{ config: PublisherConfig; refCode?: string | null }> = ({
  config,
  refCode,
}) => {
  const name = displayedName(config);
  const minBudget = `$${Number(config.minBudget ?? 0).toLocaleString('en-US')}`;

  return (
    <MaterialSheet
      config={config}
      variant="brochure"
      kicker={<>For our advertisers<br />Streaming TV, self-serve</>}
      footNote={`© ${name} · Streaming TV placements in your local market`}
    >
      <div className="msheet-eyebrow">New from {name}</div>
      <h1>
        <span>Advertise on streaming TV.</span>
        <em>In 3 steps.</em>
      </h1>
      <p className="msheet-sub">
        Built for local businesses. <b>Budgets start at {minBudget}.</b>
      </p>

      <div className="msheet-mid">
        <div className="msheet-steps msheet-steps--cards">
          <div className="msheet-step">
            <div className="msheet-num">1</div>
            <h3>Self-serve, low cost</h3>
            <p>You control your budget and flight dates.</p>
          </div>
          <div className="msheet-step">
            <div className="msheet-num">2</div>
            <h3>We make your ad</h3>
            <p>Free. Built from your website in about five minutes.</p>
          </div>
          <div className="msheet-step">
            <div className="msheet-num">3</div>
            <h3>Credit card purchase</h3>
            <p>Simple, turnkey, you&rsquo;re in control.</p>
          </div>
        </div>

        <div className="msheet-why">
          <div>
            <div className="msheet-why-label">Why streaming TV</div>
            {/* Uses the station's external (display) name. This started as a
                stub for KITV's "Your On Island News", which couldn't be
                hardcoded across stations — naming the station directly gets
                the same local specificity from config, with no new field. */}
            <h4>You&rsquo;re on {name}</h4>
            <p>Where our viewers watch: Roku, Fire TV, Apple TV, and more</p>
          </div>
          <div>
            <div className="msheet-why-label">Why us</div>
            <h4>Your local team</h4>
            <p>The local news team your customers watch each day.</p>
          </div>
        </div>

        <div className="msheet-chips">
          <div className="msheet-chip">Set up in minutes</div>
          <div className="msheet-chip msheet-chip--hi">Free commercial included</div>
          <div className="msheet-chip">Track every play</div>
          <div className="msheet-chip">Pay upfront or monthly</div>
        </div>

        <div className="msheet-cta">
          <div className="msheet-cta-text">
            <div className="msheet-cta-label">Start here</div>
            <div className="msheet-cta-main">
              <strong>Be on TV this week.</strong>
              <a href={storefrontHref(config.slug, refCode)} target="_blank" rel="noreferrer">
                {storefrontUrl(config.slug, refCode)}
              </a>
            </div>
          </div>
          <div className="msheet-cta-qr">
            <QRCodeSVG value={storefrontHref(config.slug, refCode)} size={78} level="M" />
          </div>
        </div>
      </div>
    </MaterialSheet>
  );
};

export default AdvertiserOnePagerV2;
